import React from 'react';
import { MapPin, Mail, Phone, Globe, Clock, Linkedin, Facebook } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-r from-primary-800 to-primary-900 text-white mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/logo.png" 
                alt="MHS Logo" 
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="text-xl font-bold">MHS</h3>
            </div>
            <p className="text-primary-100 text-sm leading-relaxed">
              Mining and Hydraulics Supplies - Leading provider of hydraulic solutions and mining equipment services.
            </p>
            <div className="flex items-center gap-2 text-primary-200">
              <Globe className="w-4 h-4" />
              <a href="https://miningandhydraulics.com.au/" className="text-sm hover:text-white transition-colors">
                miningandhydraulics.com.au
              </a>
            </div>
            
            {/* Social Media Links */}
            <div className="mt-4">
              <p className="text-sm font-medium text-white mb-2">Follow Us</p>
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.linkedin.com/company/mining-&-hydraulic-supplies" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-200 hover:text-white transition-colors"
                  aria-label="Visit our LinkedIn page"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.facebook.com/miningandhydraulics" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-200 hover:text-white transition-colors"
                  aria-label="Visit our Facebook page"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-6">Contact Information</h3>
            
            <div className="flex items-start gap-3 text-primary-100">
              <MapPin className="w-5 h-5 mt-0.5 text-primary-300" />
              <div className="text-sm">
                <p className="font-medium text-white">Head Office</p>
                <p>11 Boulder Rd</p>
                <p>Malaga, Perth WA 6090</p>
              </div>
            </div>

            <div className="flex items-start gap-3 text-primary-100">
              <MapPin className="w-5 h-5 mt-0.5 text-primary-300" />
              <div className="text-sm">
                <p className="font-medium text-white">Workshop</p>
                <p>19 Boulder Rd</p>
                <p>Malaga, Perth WA 6090</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-primary-100">
              <Phone className="w-5 h-5 text-primary-300" />
              <div className="text-sm">
                <a href="tel:+61892492511" className="hover:text-white transition-colors">
                  (+61 8) 9249 2511
                </a>
              </div>
            </div>

            <div className="flex items-center gap-3 text-primary-100">
              <Mail className="w-5 h-5 text-primary-300" />
              <div className="text-sm">
                <a href="mailto:sales@mhs.com.au" className="hover:text-white transition-colors">
                  sales@mhs.com.au
                </a>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-6">Business Hours</h3>
            
            <div className="flex items-start gap-3 text-primary-100">
              <Clock className="w-5 h-5 mt-0.5 text-primary-300" />
              <div className="text-sm">
                <p className="font-medium text-white">Operating Hours</p>
                <p>8:00 AM to 5:00 PM</p>
                <p>Monday to Friday</p>
                <p className="text-primary-300 mt-1">Closed public holidays</p>
              </div>
            </div>
          </div>

          {/* ISO Certifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-6">Quality Certifications</h3>
            
            <div className="grid grid-cols-1 gap-6">
              {/* ISO 9001 */}
              <div className="text-center">
                <img 
                  src="/ISO9001.jpg" 
                  alt="ISO 9001 Certification" 
                  className="w-32 h-24 object-contain mx-auto mb-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="w-32 h-24 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2 hidden">
                  <span className="text-xs font-bold text-white">ISO 9001</span>
                </div>
                <p className="text-xs text-primary-200">ISO 9001</p>
                <p className="text-xs text-primary-300">Quality Management</p>
              </div>

              {/* ISO 45001 */}
              <div className="text-center">
                <img 
                  src="/ISO45001.jpg" 
                  alt="ISO 45001 Certification" 
                  className="w-32 h-24 object-contain mx-auto mb-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="w-32 h-24 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2 hidden">
                  <span className="text-xs font-bold text-white">ISO 45001</span>
                </div>
                <p className="text-xs text-primary-200">ISO 45001</p>
                <p className="text-xs text-primary-300">Health & Safety</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Border */}
        <div className="border-t border-primary-700 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-primary-300 text-sm">
              © 2025 MHS | Mining and Hydraulics Supplies ABN 29 559 250 199
            </p>
            <div className="flex gap-6 text-sm text-primary-300">
              <a href="https://miningandhydraulics.com.au/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="https://miningandhydraulics.com.au/terms-conditions/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
